import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { INITIAL_FORM_STATE, validateAccessForm } from '../access-request/accessRequestUtils';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useAccessRequestForm = (introductionData, onClose, prefillToken, prefillFromUser) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [briefingOrigin, setBriefingOrigin] = useState(null);

  // Prefill from the currently signed-in user (e.g. gateway user requesting
  // an invitation from inside the app). Runs whenever the passed profile
  // reference changes; fields set to empty are kept unchanged.
  useEffect(() => {
    if (!prefillFromUser || typeof prefillFromUser !== 'object') return;
    const name = prefillFromUser.name || '';
    const parts = name.trim().split(/\s+/);
    const first = parts[0] || '';
    const last = parts.slice(1).join(' ') || '';
    setFormData(prev => ({
      ...prev,
      first_name: prev.first_name || first,
      last_name: prev.last_name || last,
      email: prev.email || prefillFromUser.email || '',
      country: prev.country || prefillFromUser.country || '',
      phone_country_code: prev.phone_country_code || prefillFromUser.phone_country_code || '',
      phone_number: prev.phone_number || prefillFromUser.phone_number || '',
      occupation: prev.occupation || prefillFromUser.occupation || '',
      linkedin_profile: prev.linkedin_profile || prefillFromUser.linkedin_profile || '',
    }));
  }, [prefillFromUser]);

  // Briefing pre-fill (from converted private briefing)
  useEffect(() => {
    if (!prefillToken) return;
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/briefings/prefill/${prefillToken}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        setBriefingOrigin({ briefing_id: data.briefing_id });
        setFormData(prev => ({
          ...prev,
          first_name: data.first_name || prev.first_name,
          last_name: data.last_name || prev.last_name,
          email: data.email || prev.email,
          occupation: data.organisation || prev.occupation, // best-fit field
          briefing_id: data.briefing_id,
        }));
      })
      .catch(err => logger.error('Briefing prefill error:', err));
    return () => { cancelled = true; };
  }, [prefillToken]);

  useEffect(() => {
    if (introductionData) {
      const names = introductionData.recipient_name.split(' ');
      setFormData(prev => ({
        ...prev,
        first_name: names[0] || '',
        last_name: names.slice(1).join(' ') || '',
        email: introductionData.recipient_email,
        introduced_by: introductionData.introducer_name,
        introducer_type: introductionData.introducer_type,
        introducer_id: introductionData.introduction_id,
        relationship: introductionData.relationship
      }));
    }
  }, [introductionData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (value) => {
    setFormData(prev => {
      const currentTypes = prev.investment_types || [];
      return currentTypes.includes(value)
        ? { ...prev, investment_types: currentTypes.filter(t => t !== value) }
        : { ...prev, investment_types: [...currentTypes, value] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateAccessForm(formData)) return;
    setIsSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/access-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to submit request');
      setIsSuccess(true);
      toast.success('Access request submitted successfully!');
    } catch (error) {
      logger.error('Access request error:', error);
      toast.error(error.message || 'Failed to submit access request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData(INITIAL_FORM_STATE);
      setIsSuccess(false);
      onClose();
    }
  };

  return {
    formData, isSubmitting, isSuccess, briefingOrigin,
    handleInputChange, handleCheckboxChange,
    handleSubmit, handleClose,
  };
};
